import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  Dialog,
  FormItem,
  FormRow,
  InfoAlert,
  Input,
  SecondaryBtn,
} from '@tmapy/style-guide';

import { QUERY_STATE } from '../../constants';

import { getTodayDate } from '../utils';

import {
  serviceOrderBtnMessages,
  serviceOrderDetailMessages,
  serviceOrderMessages,
} from './serviceOrderMessages';

type ServiceOrderPaidDialogProps = {
  queryState: QUERY_STATE;
  onConfirm(taxableSupplyDate: string): void;
  onClose(): void;
};

/** DUZP se nabízí na dnešek, pracovnice ho může přepsat. Server ho vyžaduje vždy. */
export const ServiceOrderPaidDialog: React.FC<ServiceOrderPaidDialogProps> = ({
  queryState,
  onConfirm,
  onClose,
}) => {
  const formatMessage = useMessage();
  /** Dialog žije krátce, stačí spočítat jednou — drží i horní mez pole. */
  const today = useMemo(() => getTodayDate(), []);
  const [date, setDate] = useState(today);
  const [validate, setValidate] = useState(false);

  const isSaving = queryState === QUERY_STATE.WAITING;
  // `max` na poli hlídá jen výběr v kalendáři, ručně zapsané datum projde dál.
  const errorMessage = !date
    ? formatMessage(serviceOrderDetailMessages.taxableSupplyDateError)
    : date > today
      ? formatMessage(serviceOrderDetailMessages.taxableSupplyDateFutureError)
      : null;

  const handleChange = useCallback((_e: React.SyntheticEvent, eventData?: { value: string }) => {
    setDate(eventData?.value ?? '');
  }, []);

  const handleConfirm = useCallback(() => {
    setValidate(true);
    if (errorMessage) return;
    onConfirm(date);
  }, [date, errorMessage, onConfirm]);

  return (
    <Dialog width='480px' onEscKeyDown={onClose} onOverlayClick={onClose}>
      <div className='sg-a-p-2 sg-u-vs-2'>
        <h2 className='sg-u-type-h2'>
          <FormattedMessage {...serviceOrderDetailMessages.paidTitle} />
        </h2>
        <InfoAlert>
          <FormattedMessage {...serviceOrderDetailMessages.paidDialogInfo} />
        </InfoAlert>
        <FormRow>
          <FormItem
            label={formatMessage(serviceOrderMessages.taxableSupplyDate)}
            isRequired
            errorMessage={validate ? errorMessage : null}
          >
            <Input
              type='date'
              name='taxable_supply_date'
              value={date}
              max={today}
              isDisabled={isSaving}
              isInvalid={validate && !!errorMessage}
              onChange={handleChange}
            />
          </FormItem>
        </FormRow>
        {queryState === QUERY_STATE.FAIL && (
          <DangerAlert>
            <FormattedMessage {...serviceOrderDetailMessages.paidFail} />
          </DangerAlert>
        )}
        <BtnBar>
          <CtaBtn isDisabled={isSaving} onClick={handleConfirm}>
            <FormattedMessage {...serviceOrderBtnMessages.paid} />
          </CtaBtn>
          <SecondaryBtn isDanger isDisabled={isSaving} onClick={onClose}>
            <FormattedMessage {...serviceOrderBtnMessages.cancel} />
          </SecondaryBtn>
        </BtnBar>
      </div>
    </Dialog>
  );
};
